import React, { FC, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { RootState } from 'MyTypes';
import { intersection, isEmpty } from 'lodash/fp';

import { Alert, FlexRow, Form } from '@portal/common/components';
import {
    AGE_GROUP_ID_KEY,
    CASE_HOSPITALIZATION_ID_KEY,
    CASE_OUTCOME_ID_KEY,
    CASE_SEVERITY_ID_KEY,
    GENDER_ID_KEY,
    INFECTION_TYPE_ID_KEY,
    LOCATION_ID_KEY,
    PRIMARY_ENTITY_ID_KEY,
    ROUND_ID_KEY,
    SEROTYPE_ID_KEY,
    VACCINATION_STATUS_ID_KEY,
    WOLBACHIA_INCLUDED_ID_KEY,
    YEAR_KEY,
    YEAR_START_KEY,
} from '@portal/common/models/data-key';
import { TakedaDengueRecords } from '@portal/common/models/data-type';
import { getDataTypeConfig } from '@portal/common/models/data-type-config';
import { styled } from '@portal/common/theme';
import {
    DataCondition,
    DataGranularity,
    DataGranularityKey,
    RefinementFilterConfig,
} from '@portal/common/types';

import RefinementFilter from '../../components/DataExplorer/RefinementFilter';
import RunQueryButton from '../../components/RunQueryButton';
import _ from '../../locale';
import {
    dataSelectionValidation,
    validateSelectedConditionsComplexityLimit,
} from '../../store/data-explorer/actions';
import {
    getCombinedSelectedFiltersCount,
    getSelectedConditions,
    getSelectedConditionsDataTypesGranularity,
    getSelectedConditionsRefinedGranularity,
    getSelectedRefinementFiltersWithFallbackToDefaultFilters,
    getVisibleRefinementFilters,
    hasErrorInRefinements,
    hasFiltersChangedSinceLastRequest,
    isDataLoading,
} from '../../store/data-explorer/selectors';
import usePrevious from '../../utility/hook/use-previous';
import sortRefinementsOptions from '../../utility/sort-refinements-options';

const REFINEMENT_OPTIONS = [
    CASE_OUTCOME_ID_KEY,
    CASE_HOSPITALIZATION_ID_KEY,
    ...sortRefinementsOptions([
        LOCATION_ID_KEY,
        AGE_GROUP_ID_KEY,
        GENDER_ID_KEY,
        ROUND_ID_KEY,
        INFECTION_TYPE_ID_KEY,
        SEROTYPE_ID_KEY,
        VACCINATION_STATUS_ID_KEY,
        WOLBACHIA_INCLUDED_ID_KEY,
        YEAR_KEY,
        YEAR_START_KEY,
    ]),
] as DataGranularityKey[];

const REFINEMENT_FILTERS_CONFIG: RefinementFilterConfig = {
    [LOCATION_ID_KEY]: { isMulti: true },
    [AGE_GROUP_ID_KEY]: { isMulti: true },
    [GENDER_ID_KEY]: { isMulti: true },
    [ROUND_ID_KEY]: { isMulti: true },
    [CASE_SEVERITY_ID_KEY]: { isMulti: false, isRevertToDefaultDisabled: true },
    [CASE_HOSPITALIZATION_ID_KEY]: { isMulti: false, hideSelectAll: true },
    [CASE_OUTCOME_ID_KEY]: { isMulti: false, hideSelectAll: true },
    [INFECTION_TYPE_ID_KEY]: { isMulti: true },
    [SEROTYPE_ID_KEY]: { isMulti: true },
    [VACCINATION_STATUS_ID_KEY]: { isMulti: true },
    [WOLBACHIA_INCLUDED_ID_KEY]: { isMulti: true },
    [YEAR_KEY]: { isMulti: true, undefinedWhenAllSelected: true, hideSelectAll: true },
    [YEAR_START_KEY]: { isMulti: true },
};

const Container = styled('div')({
    marginTop: 0,
    background: 'white',
});

const StyledAlert = styled(Alert)({
    marginBottom: 15,
});

const StyledForm = styled(Form)({
    marginTop: 0,
});

const FiltersWrapper = styled('div')(({ theme }) => ({
    gridTemplateColumns: '1fr',
    display: 'grid',
    gridAutoRows: 'auto',
    [theme.media.laptop]: {
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '0 8px',
        '> :nth-of-type(n+4)': {
            gridColumn: 'span 3',
        },
    },
}));

type Props = { refinementFiltersConfig?: RefinementFilterConfig };

const ONLY_SELECT_ALL_KEYS = [AGE_GROUP_ID_KEY];

const DengueScenariosFiltersControls: FC<Props> = ({
    refinementFiltersConfig = REFINEMENT_FILTERS_CONFIG,
}) => {
    const dispatch = useDispatch();

    const selectedConditions = useSelector(getSelectedConditions);
    const selectedRefinementFilters = useSelector(
        getSelectedRefinementFiltersWithFallbackToDefaultFilters
    );
    const previousSelectedRefinementFilters = usePrevious(selectedRefinementFilters);
    const granularity = useSelector(getSelectedConditionsDataTypesGranularity);
    const refinedGranularity = useSelector(getSelectedConditionsRefinedGranularity);
    const errorInRefinements = useSelector(hasErrorInRefinements);
    const isLoadingData = useSelector(isDataLoading);
    const filtersChangedSinceLastRequest = useSelector(hasFiltersChangedSinceLastRequest);
    const combinedFiltersCount = useSelector(getCombinedSelectedFiltersCount);
    const visibleRefinementFilters = useSelector(getVisibleRefinementFilters);
    const yearRefinementFilterMode = useSelector(
        (state: RootState) => state.dataExplorer.yearRefinementFilterMode
    );

    const { filterKey: primaryEntityKey } = getDataTypeConfig()[TakedaDengueRecords];

    // get available refinements based on given granularity and condition
    const getAvailableRefinementsForRefinedGranularity = (granularity: DataGranularity) =>
        REFINEMENT_OPTIONS.filter((i) => granularity.hasOwnProperty(i))
            // filter granularity with values more than 1 or that one value isn't selected
            .filter((i) => {
                const filterGranularity = granularity[i];
                const selectedValue = selectedRefinementFilters[i];
                const isSelectedValuePartOfGranularity =
                    isEmpty(selectedValue) ||
                    !isEmpty(intersection(selectedValue, filterGranularity));
                return (
                    filterGranularity != null &&
                    (filterGranularity.length > 1 ||
                        !isSelectedValuePartOfGranularity ||
                        visibleRefinementFilters.includes(i))
                );
            });

    const renderFiltersControls = useCallback(() => {
        const fallbackGranularity = refinedGranularity || granularity;
        const availableRefinementFilters =
            getAvailableRefinementsForRefinedGranularity(fallbackGranularity);
        const modes = { [YEAR_KEY]: yearRefinementFilterMode };

        return availableRefinementFilters.map((filterKey) => (
            <RefinementFilter
                hideDelete
                hideSelectAll={refinementFiltersConfig[filterKey]?.hideSelectAll}
                key={filterKey}
                refinementFiltersConfig={refinementFiltersConfig}
                granularityKey={filterKey as keyof typeof REFINEMENT_FILTERS_CONFIG}
                availableRefinementFilters={availableRefinementFilters}
                selectedValue={selectedRefinementFilters[filterKey]}
                mode={modes[filterKey]}
                onlySelectAllKeys={ONLY_SELECT_ALL_KEYS as DataGranularityKey[]}
            />
        ));
    }, [granularity, refinedGranularity, selectedRefinementFilters, yearRefinementFilterMode]);

    const handleConditionChange = useCallback(
        (conditionIds: number[]): void => {
            if (!isEmpty(conditionIds)) {
                const conditions = conditionIds.map((id) => ({
                    data_type: TakedaDengueRecords,
                    primary_entity_id: id,
                })) as DataCondition[];
                dispatch(validateSelectedConditionsComplexityLimit(conditions));
            }
        },
        [dispatch]
    );

    return (
        <Container>
            {errorInRefinements && (
                <StyledAlert color="warning" position="middle">
                    {_.get('refinement_filters_refine_selection_warning')}
                </StyledAlert>
            )}
            <StyledForm>
                <FiltersWrapper>
                    <RefinementFilter
                        hideDelete
                        hideSelectAll
                        key={PRIMARY_ENTITY_ID_KEY}
                        granularityKey={primaryEntityKey as DataGranularityKey}
                        selectedValue={selectedRefinementFilters[primaryEntityKey]}
                        availableRefinementFilters={{
                            [primaryEntityKey]: selectedRefinementFilters[primaryEntityKey],
                        }}
                        refinementFiltersConfig={refinementFiltersConfig}
                        onChange={handleConditionChange}
                    />
                    {renderFiltersControls()}
                </FiltersWrapper>
                {!!selectedConditions.length && (
                    <FlexRow align="center" justify="flex-end">
                        <RunQueryButton
                            isLoading={isLoadingData}
                            disabled={
                                isLoadingData ||
                                errorInRefinements ||
                                !filtersChangedSinceLastRequest
                            }
                            onClick={() => {
                                dispatch(
                                    dataSelectionValidation.request({
                                        conditions: selectedConditions,
                                        refinementFilters: selectedRefinementFilters,
                                    })
                                );
                            }}
                            filtersCount={combinedFiltersCount}
                        />
                    </FlexRow>
                )}
            </StyledForm>
        </Container>
    );
};

export default DengueScenariosFiltersControls;
